import { useState, useEffect } from 'react';
import { PanelHowTo, api } from './_barrel.js';


        // Analyst-triggered OSINT Industries lookup for a subject. On-demand only
        // (billed per request in credits), so nothing runs until the analyst
        // clicks. The most recent stored lookup is re-hydrated on mount (no
        // billed call). Intrusive source: a lawful-basis note is required for a
        // subject who is not the consenting principal — the server enforces it
        // and this panel surfaces that prompt. The analyst can prune irrelevant
        // platforms and opt a lookup into the exposure report.
        const OSINT_TYPES = [
            { value: 'email', label: 'Email' },
            { value: 'phone', label: 'Phone' },
            { value: 'username', label: 'Username' },
            { value: 'name', label: 'Name' },
            { value: 'wallet', label: 'Crypto wallet' },
        ];

        function OsintLookupPanel({ token, subjectId }) {
            const [state, setState] = useState('idle');   // idle | loading | done | error
            const [data, setData] = useState(null);
            const [err, setErr] = useState(null);
            const [savingInclude, setSavingInclude] = useState(false);
            const [type, setType] = useState('email');
            const [query, setQuery] = useState('');
            const [note, setNote] = useState('');
            const [premium, setPremium] = useState(false);
            const [collapsed, setCollapsed] = useState(() => {
                try { return localStorage.getItem('veil_osint_collapsed') === '1'; } catch (e) { return false; }
            });
            const toggleCollapsed = () => setCollapsed(v => {
                const next = !v;
                try { localStorage.setItem('veil_osint_collapsed', next ? '1' : '0'); } catch (e) {}
                return next;
            });

            // Re-hydrate the last stored lookup so a refresh doesn't lose it and
            // doesn't re-spend credits.
            useEffect(() => {
                let live = true;
                (async () => {
                    try {
                        const r = await api(`/api/v1/subjects/${subjectId}/osint-lookup`, {}, token);
                        if (live && r) {
                            setData(r); setState('done');
                            if (r.query_type) setType(r.query_type);
                            if (r.searched) setQuery(r.searched);
                            if (r.lawful_basis_note) setNote(r.lawful_basis_note);
                        }
                    } catch (e) { /* no prior lookup / not configured — stay idle */ }
                })();
                return () => { live = false; };
            }, [subjectId, token]);

            async function run() {
                if (!query.trim()) { setErr('Enter a selector to search on.'); setState('error'); return; }
                setState('loading'); setErr(null);
                try {
                    const q = { type, query: query.trim(), premium };
                    if (note.trim()) q.lawful_basis_note = note.trim();
                    const r = await api(`/api/v1/subjects/${subjectId}/osint-lookup`,
                        { method: 'POST', body: JSON.stringify(q) }, token);
                    setData(r); setState('done');
                } catch (e) {
                    if (e.status === 503) setErr('OSINT Industries is not configured on this deployment (no API key).');
                    else if (e.status === 402) setErr('OSINT Industries: insufficient credits on the account.');
                    else setErr((e.data && e.data.detail) || e.message);
                    setState('error');
                }
            }

            async function toggleInclude() {
                if (!data || !data.id) return;
                const next = !data.include_in_report;
                setSavingInclude(true);
                try {
                    const updated = await api(
                        `/api/v1/subjects/${subjectId}/osint-lookup/${data.id}`,
                        { method: 'PATCH', body: JSON.stringify({ include_in_report: next }) },
                        token);
                    setData(updated);
                } catch (e) {
                    setErr((e.data && e.data.detail) || e.message);
                } finally {
                    setSavingInclude(false);
                }
            }

            // Persist the analyst-curated account list (pruned of irrelevant
            // platforms). Only what's kept flows into the report.
            async function patchAccounts(nextAccounts) {
                if (!data || !data.id) return;
                try {
                    const updated = await api(
                        `/api/v1/subjects/${subjectId}/osint-lookup/${data.id}`,
                        { method: 'PATCH', body: JSON.stringify({ accounts: nextAccounts }) }, token);
                    setData(updated);
                } catch (e) { setErr((e.data && e.data.detail) || e.message); }
            }
            const removeAccount = (idx) => patchAccounts((data.accounts || []).filter((_, i) => i !== idx));

            const accounts = (data && data.accounts) || [];
            // Registered accounts are the actionable footprint; keep their index in
            // the full array so curation removes the right row.
            const registered = accounts
                .map((a, i) => ({ a, i }))
                .filter(x => x.a && x.a.registered);
            const breaches = (data && data.breaches) || [];

            const fmt = (s) => (s && String(s).trim()) ? String(s).trim() : null;

            return (
                <div className="panel mb-6">
                    <div className="panel-header is-self-collapse flex items-center justify-between">
                        <span className="font-display text-xl gold" style={{ cursor: 'pointer' }}
                              onClick={toggleCollapsed}>
                            Digital footprint · OSINT Industries
                            <span className="text-xs text-muted ml-2">{collapsed ? '▸' : '▾'}</span>
                        </span>
                        <div className="flex items-center gap-3">
                            {!collapsed && (
                                <button className="btn-gold" style={{ padding: '6px 14px', fontSize: 13 }}
                                        onClick={run} disabled={state === 'loading'}>
                                    {state === 'loading' ? 'Searching…' : (state === 'done' ? 'Search again' : 'Run lookup')}
                                </button>
                            )}
                            <button className="text-xs font-mono uppercase tracking-widest text-muted"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                    onClick={toggleCollapsed}>
                                {collapsed ? '+ Show' : '– Hide'}
                            </button>
                        </div>
                    </div>
                    <PanelHowTo id="osint" />
                    {!collapsed && (
                    <div className="px-6 py-5">
                        {/* Selector controls */}
                        <div className="flex flex-wrap gap-2 mb-2" style={{ maxWidth: 640 }}>
                            <select value={type} onChange={e => setType(e.target.value)}
                                    style={{ flex: '0 0 auto', background: 'var(--zone-2)', color: 'var(--text)', border: '1px solid var(--veil)', padding: '6px 10px', fontSize: 13 }}>
                                {OSINT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                            <input value={query} onChange={e => setQuery(e.target.value)}
                                   placeholder={type === 'email' ? 'name@example.com'
                                        : type === 'phone' ? '+44 7700 900000'
                                        : type === 'username' ? 'handle'
                                        : type === 'wallet' ? '0x… / bc1…' : 'Full name'}
                                   onKeyDown={e => { if (e.key === 'Enter') run(); }}
                                   style={{ flex: '1 1 220px', minWidth: 0, background: 'var(--zone-2)', color: 'var(--text)', border: '1px solid var(--veil)', padding: '6px 10px', fontSize: 13 }} />
                        </div>
                        <label className="flex items-center gap-2 text-xs text-muted mb-3" style={{ cursor: 'pointer' }}>
                            <input type="checkbox" checked={premium} onChange={e => setPremium(e.target.checked)} />
                            Premium modules (deeper coverage — costs additional credits)
                        </label>

                        {/* Lawful-basis note — required for third parties; the server enforces it. */}
                        <div className="mb-4" style={{ maxWidth: 640 }}>
                            <div className="font-mono text-xs uppercase tracking-widest text-muted mb-1">
                                Necessity &amp; proportionality note
                            </div>
                            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                                      placeholder="Required unless the subject is a consenting principal — why this intrusive source is necessary and proportionate for this subject."
                                      style={{ width: '100%', background: 'var(--zone-2)', color: 'var(--text)', border: '1px solid var(--veil)', padding: '6px 10px', fontSize: 13, resize: 'vertical' }} />
                        </div>

                        {state === 'idle' && (
                            <div className="text-sm text-muted" style={{ maxWidth: 640 }}>
                                Map the subject&rsquo;s cross-platform footprint and breach exposure on demand.
                                Review results before acting; nothing appears in the client report until you tick
                                &ldquo;Include in exposure report&rdquo;.
                            </div>
                        )}
                        {state === 'error' && <div className="text-sm" style={{ color: 'var(--red)' }}>{err}</div>}
                        {state === 'done' && data && (
                            <>
                                <div className="text-sm text-muted mb-4">
                                    {(data.registered_count || 0) === 0 && (data.breach_count || 0) === 0
                                        ? `No registered accounts or breaches found for ${data.searched || 'the selector'}.`
                                        : `${data.registered_count || 0} ${(data.registered_count === 1) ? 'platform' : 'platforms'}` +
                                          ` · ${data.breach_count || 0} ${(data.breach_count === 1) ? 'breach' : 'breaches'}` +
                                          (data.searched ? ` · searched ${data.searched}` : '') +
                                          (data.estimated_credits ? ` · ~${data.estimated_credits} credit${data.estimated_credits === 1 ? '' : 's'}` : '')}
                                </div>

                                {/* Registered platforms */}
                                {registered.length > 0 && (
                                    <div className="mb-5">
                                        <div className="font-mono text-xs uppercase tracking-widest text-muted mb-2">Registered platforms</div>
                                        {registered.map(({ a, i }) => (
                                            <div key={i} className="flex items-start gap-3 mb-3 pb-3"
                                                 style={{ borderBottom: '1px solid var(--line)' }}>
                                                {a.picture_url &&
                                                    <img src={a.picture_url} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', border: '1px solid var(--line)' }} />}
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div className="text-sm" style={{ color: 'var(--text)', textTransform: 'capitalize' }}>
                                                        {a.module}
                                                        {!a.reliable_source && <span className="text-xs text-muted ml-2">(unverified source)</span>}
                                                    </div>
                                                    <div className="text-xs text-muted" style={{ wordBreak: 'break-word' }}>
                                                        {[fmt(a.name), fmt(a.username) && `@${a.username.replace(/^@/, '')}`,
                                                          fmt(a.email), fmt(a.phone), fmt(a.location),
                                                          fmt(a.creation_date) && `created ${a.creation_date}`,
                                                          fmt(a.last_seen) && `last seen ${a.last_seen}`]
                                                          .filter(Boolean).join(' · ') || 'Account exists (no public detail)'}
                                                    </div>
                                                    {fmt(a.profile_url) && (
                                                        <a href={a.profile_url} target="_blank" rel="noopener noreferrer"
                                                           className="text-xs" style={{ color: 'var(--cyan)', wordBreak: 'break-all' }}>{a.profile_url}</a>
                                                    )}
                                                </div>
                                                <button onClick={() => removeAccount(i)} title="Remove — not the subject"
                                                        className="text-xs" style={{ background: 'none', border: '1px solid var(--line)', borderRadius: 4, color: 'var(--red)', cursor: 'pointer', padding: '2px 8px', flexShrink: 0 }}>
                                                    ✕ Not relevant
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Breach exposure */}
                                {breaches.length > 0 && (
                                    <div className="mb-4">
                                        <div className="font-mono text-xs uppercase tracking-widest text-muted mb-2">Breach exposure</div>
                                        {breaches.map((b, i) => (
                                            <div key={i} className="mb-2">
                                                <div className="text-sm" style={{ color: 'var(--text)' }}>
                                                    {b.name || b.domain || 'Breach'}
                                                    {b.breach_date && <span className="text-xs text-muted ml-2">{b.breach_date}</span>}
                                                </div>
                                                {b.data_classes && b.data_classes.length > 0 && (
                                                    <div className="flex flex-wrap gap-2 mt-1">
                                                        {b.data_classes.map((c, j) => (
                                                            <span key={j} className="chip" style={{ cursor: 'default' }}>{c}</span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {data.id && ((data.registered_count || 0) > 0 || (data.breach_count || 0) > 0) && (
                                    <label className="flex items-center gap-2 text-sm mt-2"
                                           style={{ cursor: savingInclude ? 'wait' : 'pointer' }}>
                                        <input type="checkbox" checked={!!data.include_in_report}
                                               onChange={toggleInclude} disabled={savingInclude} />
                                        <span style={{ color: 'var(--text)' }}>
                                            Include in exposure report
                                        </span>
                                        <span className="text-xs text-muted">
                                            — adds the digital-footprint exposure to § III of the PDF.
                                        </span>
                                    </label>
                                )}
                            </>
                        )}
                    </div>
                    )}
                </div>
            );
        }

export { OsintLookupPanel };
